/**
 * `SIMEND.DAT`'s eleven outcome sequences, transcribed.
 *
 * The screen between the last second of flying and MISSION DEBRIEFING is not
 * composed at runtime by the original either: `PS.EXE` picks one of these
 * eleven titled sequences and draws its pages. `docs/game/end-of-mission.md`
 * is the evidence trail and `packages/formats/src/simend-dat.ts` the decoder.
 *
 * **Generated from the real file, checked in, and re-checked every run.**
 * `tools/bake/test/debrief-simend.test.ts` decodes `VOLUME.006`'s `SIMEND.DAT`
 * with `decodeSimendDat` and asserts this table equals it exactly, so the
 * transcription cannot drift from the game data — the same split
 * `debrief-review-text.ts` uses.
 *
 * Three of the original's own conventions survive into the strings and are the
 * caller's to expand (`simend.ts` does):
 *
 * - `%t` is a **duration** — *"After spending %t in the hospital"*. It is not
 *   `REVIEW.DAT`'s `%t`, which is a target noun; reusing an expander across
 *   the two files would put *"a rail yard"* in a hospital.
 * - `%a` names whoever shot you down.
 * - `$` marks a **pause** inside a line: the page stops there and waits before
 *   the rest of it appears.
 *
 * The leading spaces on the *"...The time passes very slowly..."* cards are
 * the file's own, and they are how the original centres those lines — the
 * count differs between the two prison-camp sequences, which is what a hand
 * count looks like and not what a centring routine would produce. They are
 * kept verbatim.
 */

/** One page: a picture, an undecoded mode byte for the `e`/`f`/`g`/`i` family, and its prose. */
export interface SimendTextPage {
  /** One of `e` `f` `g` `i` `j` `k`; `apps/web`'s `simend-screens.ts` maps it to a sheet. */
  readonly picture: string;
  readonly mode?: number;
  readonly lines: readonly string[];
}

export interface SimendTextSequence {
  readonly title: string;
  readonly pages: readonly SimendTextPage[];
}

/** Eleven sequences, in `SIMEND.DAT`'s own order. `SIMEND_OUTCOMES` names them. */
export const SIMEND_SEQUENCES: readonly SimendTextSequence[] = [
  // 0
  {
    title: "CRASHED!",
    pages: [
      { picture: "f", mode: 0xfd, lines: ["You've crashed!  You walk away from the wreck", "without any serious injuries..."] },
      { picture: "g", mode: 0xfd, lines: ["You return to your aerodrome."] },
    ],
  },
  // 1
  {
    title: "CRASHED!!",
    pages: [
      { picture: "f", mode: 0xfd, lines: ["You've crashed!  You manage to crawl out of", "what's left of your aircraft..."] },
      { picture: "i", mode: 0xfd, lines: ["...You're taken to a nearby hospital to be", "treated for your injuries..."] },
      { picture: "k", lines: ["The time passes slowly...$", "             ...%t..."] },
      { picture: "g", mode: 0xfd, lines: ["After spending %t in the hospital,", "you're fully recovered and return", "to active duty..."] },
    ],
  },
  // 2
  {
    title: "CAPTURED!",
    pages: [
      { picture: "f", mode: 0xfd, lines: ["You've crashed in enemy territory!", "Enemy soldiers pull you from the wreck..."] },
      { picture: "i", mode: 0x2, lines: ["...You're taken to a prison camp..."] },
      { picture: "j", lines: ["  ...The time passes very slowly...$", "            ...%t..."] },
      { picture: "g", mode: 0xfd, lines: ["After spending %t in a prison camp,", "you escape, return to your aerodrome and", "report for duty!"] },
    ],
  },
  // 3
  {
    title: "CRASHED!!!",
    pages: [
      { picture: "e", mode: 0xfe, lines: [] },
      { picture: "f", mode: 0x2, lines: ["You've crashed.  You don't walk away", "from this wreck..."] },
    ],
  },
  // 4
  {
    title: "VANQUISHED!",
    pages: [
      { picture: "e", mode: 0xfe, lines: ["%a has shot you down."] },
      { picture: "f", mode: 0xfd, lines: ["He adds you to his list of many", "victories."] },
    ],
  },
  // 5
  {
    title: "VANQUISHED!",
    pages: [{ picture: "e", mode: 0xfe, lines: ["You've been shot down."] }],
  },
  // 6
  {
    title: "SAFE RETURN...",
    pages: [{ picture: "g", mode: 0xfd, lines: ["You land safely at your aerodrome."] }],
  },
  // 7
  {
    title: "SAFE RETURN...",
    pages: [
      { picture: "g", mode: 0xfd, lines: ["You land at a neighboring aerodrome. After receiving", "directions, you make it back to your own aerodrome..."] },
    ],
  },
  // 8
  {
    title: "BEHIND THE LINES",
    pages: [
      { picture: "i", mode: 0x2, lines: ["You land safely... in enemy territory!", "Enemy soldiers capture you immediately", "and lock you up in a prison camp..."] },
      { picture: "j", lines: ["   ...The time passes very slowly...$", "            ...%t..."] },
      { picture: "g", mode: 0xfd, lines: ["After spending %t in a prison camp,", "you escape, return to your aerodrome and", "report for duty!"] },
    ],
  },
  // 9
  {
    title: "SAFE LANDING",
    pages: [{ picture: "g", mode: 0xfd, lines: ["You land safely and make it back", "to your aerodrome."] }],
  },
  // 10
  {
    title: "INJURED",
    pages: [
      { picture: "i", mode: 0xfd, lines: ["You've been wounded in the air by enemy fire.", "After managing to land safely, you're taken to", "a nearby hospital to recover..."] },
      { picture: "k", lines: ["The time passes slowly...$", "             ...%t..."] },
      { picture: "g", mode: 0xfd, lines: ["After spending %t in the hospital,", "you're fully recovered and return", "to active duty..."] },
    ],
  },
];
